import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Crosshair, Loader2, MapPin, Navigation } from 'lucide-react';
import { PageSEO } from '../../components/seo/SchemaMarkup';
import { API, Eyebrow, LocatorLoading, useLocatorPageView } from './locatorShared';
import { getDistanceMiles } from '../../lib/cityCoords';

export default function LocatorNearMe() {
  useLocatorPageView();
  const [index, setIndex] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | locating | ranked | denied
  const [ranked, setRanked] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/api/locator`).then((r) => r.json()).then(setIndex).catch(() => {});
  }, []);

  const locate = () => {
    if (!navigator.geolocation) { setStatus('denied'); return; }
    setStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const withDist = index.metro_coords
          .filter((m) => m.lat && m.lng)
          .map((m) => ({ ...m, distance: Math.round(getDistanceMiles(latitude, longitude, m.lat, m.lng)) }))
          .sort((a, b) => a.distance - b.distance);
        setRanked(withDist);
        setStatus('ranked');
        if (withDist[0] && withDist[0].distance < 60) {
          setTimeout(() => navigate(`/locator/${withDist[0].slug}`), 1400);
        }
      },
      () => setStatus('denied'),
      { timeout: 10000 },
    );
  };

  if (!index) return <LocatorLoading />;

  const counts = Object.fromEntries(index.metros.map((m) => [m.slug, m.count]));

  return (
    <div className="bg-black min-h-screen px-6 pt-28 pb-16" data-testid="locator-near-me-page">
      <PageSEO
        title="Recovery Therapy Near Me — Find Your Closest Directory | NinjAthlete"
        description="Use your location to find vetted cold plunge, sauna, IV and peptide therapy facilities in your nearest metro."
        path="/locator/near-me"
      />
      <div className="max-w-3xl mx-auto text-center">
        <Eyebrow>Recovery &amp; Peptide Therapy Locator</Eyebrow>
        <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Recovery Therapy Near Me</h1>
        <p className="text-zinc-400 text-base leading-relaxed mb-8 max-w-xl mx-auto">
          Tap the button and we'll rank all {index.metros.length} metro directories by distance — then take you straight to your closest one.
        </p>

        {status !== 'ranked' && (
          <button
            onClick={locate}
            disabled={status === 'locating'}
            className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#C7FA3E] text-black text-sm font-bold uppercase tracking-widest hover:opacity-90 disabled:opacity-60 transition-opacity"
            style={{ fontFamily: 'Oswald, sans-serif' }}
            data-testid="near-me-locate-btn"
          >
            {status === 'locating' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
            {status === 'locating' ? 'Locating…' : 'Use My Location'}
          </button>
        )}
        {status === 'denied' && (
          <p className="text-zinc-500 text-sm mt-4" data-testid="near-me-denied">Location unavailable — pick your metro below instead.</p>
        )}

        {status === 'ranked' && ranked.length > 0 && (
          <div className="mt-4" data-testid="near-me-results">
            <p className="text-[#C7FA3E] text-sm mb-5">Closest directory: <strong>{ranked[0].name}</strong> ({ranked[0].distance} mi) — taking you there…</p>
            <div className="grid sm:grid-cols-3 gap-3 text-left">
              {ranked.slice(0, 6).map((m) => (
                <Link key={m.slug} to={`/locator/${m.slug}`} className="border border-zinc-800 hover:border-[#C7FA3E]/40 bg-zinc-900/60 p-4 transition-colors">
                  <p className="text-white text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>{m.name}</p>
                  <p className="text-zinc-500 text-xs mt-1 flex items-center gap-1"><Navigation className="w-3 h-3 text-[#C7FA3E]" /> {m.distance} mi · {counts[m.slug] || 0} facilities</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 text-left">
          <h2 className="text-white text-2xl mb-4 text-center" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Or Pick Your Metro</h2>
          <div className="flex flex-wrap justify-center gap-2">
            {index.metros.map((m) => (
              <Link key={m.slug} to={`/locator/${m.slug}`} className="px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-[#C7FA3E] hover:text-[#C7FA3E] text-xs uppercase tracking-wider transition-colors inline-flex items-center gap-1.5">
                <MapPin className="w-3 h-3" /> {m.name} ({m.count})
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
